/**
 * DXP registration field validation (aligned with ContentConfig / API field limits).
 */

import { unflattenWithSeparator } from "./utils";

/** JSON schema maxLength values (CompanyDetailsJson / AboutDetailsJson properties). */
export const DXP_FIELD_MAX_LENGTH = {
  personalInfo_country: 255,
  personalInfo_previouslyRegisteredEmail: 255,
  personalInfo_customJobFunction: 255,
  companyInfo_companyName: 100,
  companyInfo_website: 200,
  companyInfo_linkedin: 255,
  companyInfo_referredBy: 50,
  companyInfo_address_hqAddress: 250,
  companyInfo_companyDescription: 2000,
  companyInfo_programBenefits: 5000,
  companyInfo_address_country: 255,
  companyInfo_address_city: 40,
  companyInfo_address_zipCode: 20,
  /** Local digits (after country code), schema minLength 2 maxLength 20 */
  phoneLocalPart: 20,
  phoneLocalPartMin: 2,
} as const;

/**
 * personalInfo_phone & companyInfo_phone pattern (JSON schema).
 * Local line without country prefix.
 */
export const DXP_PHONE_LOCAL_PATTERN =
  /^[^-\sA-Za-z](\(?\+?[0-9]*\)?)?[0-9_\s\\()-]*(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;

/**
 * User profile personal phone (AboutDetailsJson) — slightly different from company line.
 */
export const DXP_PERSONAL_PHONE_PATTERN =
  /^[^-\sA-Za-z](\+?[0-9]*)?[0-9_\- ()]*(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;

/** personalInfo_previouslyRegisteredEmail (JSON schema). */
export const DXP_EMAIL_PATTERN =
  /^(?=[\w\s])\s*[-+.'\w]+@[-.\w]+\.[a-zA-Z]+\s*$/;

/**
 * Website / LinkedIn URL — same rule as companyValidationUtils (JSON website pattern).
 */
export const DXP_URL_PATTERN =
  /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))([a-zA-Z0-9_\u0080-\uFFFF-]+(\.[a-zA-Z0-9\u0080-\uFFFF-]+)+)(:\d+)?((\/)((?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+))*(\/)?(\/)?(\?.*)?(#.*)?$/;

export const DXP_NON_EMPTY_PATTERN = /^(?!\s*$).+$/;

/**
 * companyInfo_companyDescription / companyInfo_programBenefits (JSON).
 * Also allows: < > = $ € £ ₹ | [ ] { } ~ ^ ` ’ — – (U+2019, U+2014, U+2013).
 */
export const DXP_SAFE_TEXT_PATTERN =
  /^(?!\s*$)[-a-zA-Z0-9\s.,;:!?'"()\u2019_&@#%+*/\\\n\r\t<>=$€£₹|[\]{}~^`\u2014\u2013]+$/;

/** Minimum length for “Additional information” text areas (company description & program benefits). */
export const DXP_COMPANY_ADDITIONAL_INFO_MIN_LENGTH = 20;

/** Shown when company LinkedIn URL is missing host/path rules (keep in sync with CompanyInformation UI). */
export const DXP_LINKEDIN_URL_VALIDATION_MESSAGE =
  "Enter a valid LinkedIn profile or company URL (e.g. https://www.linkedin.com/in/your-profile).";

/** Company Information — number of employees (values match UI picker / API). */
export const DXP_COMPANY_NUMBER_OF_EMPLOYEES_OPTIONS = [
  "1-9 Employees",
  "10-49 Employees",
  "50-99 Employees",
  "100-499 Employees",
  "500-999 Employees",
  "1,000-14,999 Employees",
  "15,000+ Employees",
] as const;

export type CompanyValidationError = {
  instancePath: string;
  message: string;
};

export const COMPANY_INFO_PATH_PREFIX = "/companyInfo_";

/**
 * Optional flattened hint from CompanyInformation when `getStates(country)` returned
 * no rows — `companyInfo_address_state` may be empty without violating required rules.
 */
export const COMPANY_INFO_ADDRESS_STATE_PICKLIST_EMPTY =
  "companyInfo_address_statePicklistEmpty";

const ADDRESS_FIELD_KEYS = [
  "companyInfo_address_hqAddress",
  "companyInfo_address_country",
  "companyInfo_address_state",
  "companyInfo_address_city",
  "companyInfo_address_zipCode",
] as const;

function isAddressFieldInScope(
  formData: Record<string, unknown>,
  key: string,
): boolean {
  if (
    !ADDRESS_FIELD_KEYS.includes(key as (typeof ADDRESS_FIELD_KEYS)[number])
  ) {
    return true;
  }
  return formData?.companyInfo_addressChoice !== "Parent Address";
}

function trimmed(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  return String(v).trim();
}

function exceedsMaxLen(value: string, max: number): boolean {
  return value.length > max;
}

/** Extract digits/local part after last "-" in stored phone like "+1-5551234567". */
export function extractPhoneLocalPart(combinedPhone: string): string {
  const t = combinedPhone.trim();
  const idx = t.lastIndexOf("-");
  if (idx === -1) return t;
  return t.slice(idx + 1).trim();
}

/** Dial code for picker labels / search (e.g. "91" → "+91"); falls back to ISO. */
export function formatPhoneDialDisplay(
  phoneCode: string | undefined,
  countrycode: string,
): string {
  if (phoneCode?.trim()) {
    const t = phoneCode.trim();
    return t.startsWith("+") ? t : `+${t}`;
  }
  return countrycode;
}

/** Fused prefix beside the number field: only real dial codes, not ISO (legacy renderer). */
export function formatDialPrefixOnly(phoneCode: string | undefined): string {
  if (!phoneCode?.trim()) return "";
  const t = phoneCode.trim();
  return t.startsWith("+") ? t : `+${t}`;
}

/**
 * DXP ref `AboutDetailsJson` / `PartnerDetailsForm` default. When false, ref drops nested
 * `personalInfo.address`. DXP does not expose a “different location” control — keep false.
 */
export function applyDxpPersonalInfoRegisterDefaults(payload: {
  personalInfo?: unknown;
}): void {
  const pi = payload.personalInfo;
  if (pi == null || typeof pi !== "object" || Array.isArray(pi)) return;
  (pi as Record<string, unknown>).isDifferentLocation = false;
}

export function validateDxpUrl(value: string): boolean {
  return DXP_URL_PATTERN.test(value.trim());
}

/**
 * Company website: must parse as http(s) URL with a real hostname (e.g. example.com).
 */
export function validateCompanyWebsiteUrl(value: string): boolean {
  const raw = value.trim();
  if (!raw || /\s/.test(raw)) return false;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  if (!host || !host.includes(".")) return false;
  if (host === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return false;
  }
  return true;
}

/** LinkedIn profile / company URL shape (matches legacy partner-registration validation). */
export const DXP_LINKEDIN_PROFILE_URL_PATTERN =
  /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))([a-zA-Z0-9_\u0080-\uFFFF-]+(\.[a-zA-Z0-9\u0080-\uFFFF-]+)+)(:\d+)?((\/)((?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+))*(\/)?(\?((?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+=(?:(?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+)(&(?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+=(?:(?:[a-zA-Z0-9_\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})+))*)?)?(#(?:(?:[a-zA-Z0-9_.\u0080-\uFFFF-]|%[0-9A-Fa-f]{2})*)?)?$/;

export function validateLinkedInProfileUrl(value: string): boolean {
  const raw = value.trim();
  if (!raw || /\s/.test(raw)) return false;
  return DXP_LINKEDIN_PROFILE_URL_PATTERN.test(raw);
}

export function validateDxpPhoneLocalLine(value: string): boolean {
  const v = value.trim();
  if (v.length < DXP_FIELD_MAX_LENGTH.phoneLocalPartMin) return false;
  if (v.length > DXP_FIELD_MAX_LENGTH.phoneLocalPart) return false;
  return DXP_PHONE_LOCAL_PATTERN.test(v);
}

export function validateDxpPersonalPhoneLine(value: string): boolean {
  const v = value.trim();
  if (v.length < DXP_FIELD_MAX_LENGTH.phoneLocalPartMin) return false;
  if (v.length > DXP_FIELD_MAX_LENGTH.phoneLocalPart) return false;
  return DXP_PERSONAL_PHONE_PATTERN.test(v);
}

export function validateDxpEmail(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (v.length > DXP_FIELD_MAX_LENGTH.personalInfo_previouslyRegisteredEmail) {
    return false;
  }
  return DXP_EMAIL_PATTERN.test(v);
}

export function validateDxpSafeMultiline(value: string): boolean {
  return DXP_SAFE_TEXT_PATTERN.test(value.trim());
}

/** UI + validateCompanyInfoFormData — additional-info multiline field (description / benefits). */
export function getDxpAdditionalInfoFieldError(
  value: string,
  maxLength: number,
  options: { required: boolean },
): string | null {
  const v = trimmed(value);
  if (!v) {
    return options.required ? "This field is required." : null;
  }
  if (exceedsMaxLen(v, maxLength)) {
    return "Invalid characters added";
  }
  if (!validateDxpSafeMultiline(v)) {
    return "Invalid characters. Use letters, numbers, and common punctuation only.";
  }
  return null;
}

export function validateDxpRegistrationEmail(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return DXP_EMAIL_PATTERN.test(v);
}

export function validateDxpReferredByOptional(value: string): {
  valid: boolean;
  message: string;
} {
  const v = trimmed(value);
  if (!v) return { valid: true, message: "" };
  if (v.length > DXP_FIELD_MAX_LENGTH.companyInfo_referredBy) {
    return { valid: false, message: "Invalid information" };
  }
  if (!DXP_EMAIL_PATTERN.test(v)) {
    return { valid: false, message: "Invalid information" };
  }
  return { valid: true, message: "" };
}

/**
 * Company Information step — same rules as CompanyDetailsJson + legacy validateCompanyInfo.
 * Returns errors with instancePath `/companyInfo_*` for compatibility with DOM helpers.
 */
export function validateCompanyInfoFormData(
  formData: Record<string, unknown> | null | undefined,
): CompanyValidationError[] {
  if (!formData || typeof formData !== "object") {
    return [
      {
        instancePath: "/companyInfo_companyName",
        message: "Invalid Company Name",
      },
    ];
  }

  const errors: CompanyValidationError[] = [];

  const push = (key: string, message: string) => {
    if (!errors.some((e) => e.instancePath === `/${key}`)) {
      errors.push({ instancePath: `/${key}`, message });
    }
  };

  // company name
  const companyName = trimmed(formData.companyInfo_companyName);
  if (!companyName) {
    push("companyInfo_companyName", "Invalid Company Name");
  } else if (
    exceedsMaxLen(companyName, DXP_FIELD_MAX_LENGTH.companyInfo_companyName)
  ) {
    push("companyInfo_companyName", "Invalid Company Name");
  } else if (!DXP_NON_EMPTY_PATTERN.test(companyName)) {
    push("companyInfo_companyName", "Invalid Company Name");
  }

  // website
  const website = trimmed(formData.companyInfo_website);
  if (!website) {
    push("companyInfo_website", "Invalid Website URL");
  } else if (exceedsMaxLen(website, DXP_FIELD_MAX_LENGTH.companyInfo_website)) {
    push("companyInfo_website", "Invalid Website URL");
  } else if (!validateCompanyWebsiteUrl(website)) {
    push(
      "companyInfo_website",
      "Enter a valid website URL (e.g. https://www.example.com).",
    );
  }

  // phone (combined dial + local, same as JSON CustomPhoneNumber stored value)
  const phoneCombined = trimmed(formData.companyInfo_phone);
  if (!phoneCombined) {
    push("companyInfo_phone", "Invalid Phone number");
  } else if (!DXP_PHONE_LOCAL_PATTERN.test(phoneCombined)) {
    push("companyInfo_phone", "Invalid Phone number");
  } else {
    const localPart = extractPhoneLocalPart(phoneCombined);
    if (
      localPart.length < DXP_FIELD_MAX_LENGTH.phoneLocalPartMin ||
      localPart.length > DXP_FIELD_MAX_LENGTH.phoneLocalPart
    ) {
      push("companyInfo_phone", "Invalid Phone number");
    }
  }

  // linkedin (required in JSON)
  const linkedin = trimmed(formData.companyInfo_linkedin);
  if (!linkedin) {
    push("companyInfo_linkedin", "Invalid LinkedIn");
  } else if (
    exceedsMaxLen(linkedin, DXP_FIELD_MAX_LENGTH.companyInfo_linkedin)
  ) {
    push("companyInfo_linkedin", "Invalid LinkedIn");
  } else if (!validateLinkedInProfileUrl(linkedin)) {
    push("companyInfo_linkedin", DXP_LINKEDIN_URL_VALIDATION_MESSAGE);
  }

  // primary business & ownership
  if (!trimmed(formData.companyInfo_primaryBusiness)) {
    push("companyInfo_primaryBusiness", "This field is required.");
  }
  if (!trimmed(formData.companyInfo_ownership)) {
    push("companyInfo_ownership", "This field is required.");
  }

  const employeeBand = trimmed(
    String(formData.companyInfo_totalEmployees ?? ""),
  );
  if (!employeeBand) {
    push("companyInfo_totalEmployees", "This field is required.");
  } else if (
    !(DXP_COMPANY_NUMBER_OF_EMPLOYEES_OPTIONS as readonly string[]).includes(
      employeeBand,
    )
  ) {
    push("companyInfo_totalEmployees", "Please select a valid option.");
  }

  // referred by (optional email)
  const referred = validateDxpReferredByOptional(
    String(formData.companyInfo_referredBy ?? ""),
  );
  if (!referred.valid) {
    push("companyInfo_referredBy", referred.message);
  }

  // description & benefits
  const desc = trimmed(formData.companyInfo_companyDescription);
  if (!desc) {
    push("companyInfo_companyDescription", "This field is required.");
  }
  // else if (desc.length < DXP_COMPANY_ADDITIONAL_INFO_MIN_LENGTH) {
  //   push(
  //     "companyInfo_companyDescription",
  //     `Please enter at least ${DXP_COMPANY_ADDITIONAL_INFO_MIN_LENGTH} characters.`,
  //   );
  // }
  else if (
    exceedsMaxLen(desc, DXP_FIELD_MAX_LENGTH.companyInfo_companyDescription)
  ) {
    push("companyInfo_companyDescription", "Invalid characters added");
  } else if (!validateDxpSafeMultiline(desc)) {
    push(
      "companyInfo_companyDescription",
      "Invalid characters. Use letters, numbers, and common punctuation only.",
    );
  }

  const benefits = trimmed(formData.companyInfo_programBenefits);
  if (!benefits) {
    push("companyInfo_programBenefits", "This field is required.");
  }
  // else if (benefits.length < DXP_COMPANY_ADDITIONAL_INFO_MIN_LENGTH) {
  //   push(
  //     "companyInfo_programBenefits",
  //     `Please enter at least ${DXP_COMPANY_ADDITIONAL_INFO_MIN_LENGTH} characters.`,
  //   );
  // }
  else if (
    exceedsMaxLen(benefits, DXP_FIELD_MAX_LENGTH.companyInfo_programBenefits)
  ) {
    push("companyInfo_programBenefits", "Invalid characters added");
  } else if (!validateDxpSafeMultiline(benefits)) {
    push(
      "companyInfo_programBenefits",
      "Invalid characters. Use letters, numbers, and common punctuation only.",
    );
  }

  // address fields (skip when Parent Address)
  const addressChecks: {
    key: (typeof ADDRESS_FIELD_KEYS)[number];
    emptyMsg: string;
    patternMsg: string;
    max: number;
    useSafe?: boolean;
  }[] = [
    {
      key: "companyInfo_address_hqAddress",
      emptyMsg: "Invalid Address",
      patternMsg: "Invalid Address",
      max: DXP_FIELD_MAX_LENGTH.companyInfo_address_hqAddress,
    },
    {
      key: "companyInfo_address_city",
      emptyMsg: "Invalid City",
      patternMsg: "Invalid City name",
      max: DXP_FIELD_MAX_LENGTH.companyInfo_address_city,
    },
    {
      key: "companyInfo_address_zipCode",
      emptyMsg: "Invalid Zip Code",
      patternMsg: "Invalid Zip/Postal Code",
      max: DXP_FIELD_MAX_LENGTH.companyInfo_address_zipCode,
    },
  ];

  addressChecks.forEach(({ key, emptyMsg, patternMsg, max }) => {
    if (!isAddressFieldInScope(formData, key)) return;
    const val = trimmed(formData[key]);
    if (!val) {
      push(key, emptyMsg);
    } else if (exceedsMaxLen(val, max)) {
      push(key, patternMsg);
    } else if (!DXP_NON_EMPTY_PATTERN.test(val)) {
      push(key, patternMsg);
    }
  });

  if (isAddressFieldInScope(formData, "companyInfo_address_country")) {
    const c = trimmed(formData.companyInfo_address_country);
    if (!c) {
      push("companyInfo_address_country", "This field is required.");
    } else if (
      exceedsMaxLen(c, DXP_FIELD_MAX_LENGTH.companyInfo_address_country)
    ) {
      push("companyInfo_address_country", "This field is required.");
    }
  }

  if (isAddressFieldInScope(formData, "companyInfo_address_state")) {
    const s = trimmed(formData.companyInfo_address_state);
    const picklistEmpty =
      trimmed(
        String(formData[COMPANY_INFO_ADDRESS_STATE_PICKLIST_EMPTY] ?? ""),
      ) === "true";
    if (!picklistEmpty && !s) {
      push("companyInfo_address_state", "This field is required.");
    }
  }

  return errors.filter((e) => e.instancePath.startsWith("/companyInfo_"));
}

/**
 * Same rules as {@link validateCompanyInfoFormData}, but omits **empty-field** errors for
 * the two additional-info multiline fields only (save-exit may persist blanks). Non-empty
 * invalid values are still validated.
 */
export function validateCompanyInfoFormDataForSaveExit(
  formData: Record<string, unknown> | null | undefined,
): CompanyValidationError[] {
  return validateCompanyInfoFormData(formData).filter((err) => {
    if (err.instancePath === "/companyInfo_companyDescription") {
      return trimmed(formData?.companyInfo_companyDescription) !== "";
    }
    if (err.instancePath === "/companyInfo_programBenefits") {
      return trimmed(formData?.companyInfo_programBenefits) !== "";
    }
    return true;
  });
}

export function filterCompanyValidationErrors(
  errors: CompanyValidationError[],
): CompanyValidationError[] {
  return errors.filter((e) => e.instancePath.startsWith("/companyInfo_"));
}

/** AboutDetailsJson / User profile step — field keys match UserProfile state errors. */
export type DxpPersonalProfileInput = {
  countryCode: string;
  phoneDialCountryCode: string;
  phoneWithoutCountryCode: string;
  timeZone: string;
  previouslyRegistered: boolean;
  previouslyRegisteredEmail: string;
  jobLevel: string;
  jobFunction: string;
  customJobFunction: string;
  primaryRoleWithAdobe: string[];
  agreeToTos: boolean;
};

export function validateDxpPersonalProfileFields(
  input: DxpPersonalProfileInput,
): Record<string, string> {
  const e: Record<string, string> = {};
  if (!input.countryCode) e.country = "This field is required.";
  if (!input.phoneDialCountryCode) {
    e.phone = "Select a country code for your phone number.";
  } else if (!input.phoneWithoutCountryCode?.trim()) {
    e.phone = "This field is required.";
  } else if (!validateDxpPersonalPhoneLine(input.phoneWithoutCountryCode)) {
    e.phone = "Enter a valid phone number.";
  }
  if (!input.timeZone) e.timeZone = "This field is required.";
  if (input.previouslyRegistered) {
    if (!input.previouslyRegisteredEmail.trim()) {
      e.previouslyRegisteredEmail = "This field is required.";
    } else if (!validateDxpEmail(input.previouslyRegisteredEmail)) {
      e.previouslyRegisteredEmail = "Enter a valid email address.";
    }
  }
  if (!input.jobLevel) e.jobLevel = "This field is required.";
  if (!input.jobFunction) e.jobFunction = "This field is required.";
  if (input.jobFunction === "Other") {
    if (!input.customJobFunction.trim()) {
      e.customJobFunction = "This field is required.";
    } else if (/^\s*$/.test(input.customJobFunction)) {
      e.customJobFunction = "Invalid information";
    } else if (
      input.customJobFunction.trim().length >
      DXP_FIELD_MAX_LENGTH.personalInfo_customJobFunction
    ) {
      e.customJobFunction = "Invalid information";
    } else if (!DXP_NON_EMPTY_PATTERN.test(input.customJobFunction.trim())) {
      e.customJobFunction = "Invalid information";
    }
  }
  if (!input.primaryRoleWithAdobe || input.primaryRoleWithAdobe.length === 0) {
    e.primaryRoleWithAdobe = "This field is required.";
  }
  if (!input.agreeToTos) {
    e.agreeToTos = "You must accept the Terms of Use to continue.";
  }
  return e;
}

type BuildDxpSecondaryRegistrationPayloadArgs = {
  personalFlat: Record<string, unknown>;
  accountId: string;
  companyName?: string | null;
  membershipType?: string | null;
  sessionFormData?: Record<string, unknown> | null;
};

/**
 * Build POST /partner/register body for joining an existing company (Secondary).
 * Mirrors legacy PartnerDetailsForm.submitRegistrationForm for about-details Register.
 */
export function buildDxpSecondaryRegistrationPayload({
  personalFlat,
  accountId,
  companyName,
  membershipType,
  sessionFormData,
}: BuildDxpSecondaryRegistrationPayloadArgs): Record<string, unknown> {
  const data = JSON.parse(
    JSON.stringify({ ...(sessionFormData || {}), ...personalFlat }),
  ) as Record<string, unknown>;

  delete data.step;
  delete data.type;
  delete data.affiliatedCompanyName;
  delete data.previouslyRegistered;

  if (!data.personalInfo_previouslyRegistered) {
    delete data.personalInfo_previouslyRegistered;
    delete data.personalInfo_previouslyRegisteredEmail;
  }

  data.action = "register";
  data.registrationType = "Secondary";
  data.selectedRegisterCompanyType = null;
  data.companyInfo_isSubsidiary = false;

  if (data.personalInfo_jobFunction !== "Other") {
    delete data.personalInfo_customJobFunction;
  }
  delete data.personalInfo_initialPhone;
  delete data.personalInfo_initialPhoneCountryCode;
  delete data.personalInfo_phoneWithoutCountryCode;

  const payload = unflattenWithSeparator(data, "_") as Record<string, unknown>;
  applyDxpPersonalInfoRegisterDefaults(payload);

  const companyInfo = (payload.companyInfo as Record<string, unknown>) || {};
  companyInfo.redirectTo = "membershipdetails";
  companyInfo.isSubsidiary = false;
  companyInfo.id = accountId;
  if (companyName) companyInfo.companyName = companyName;
  if (membershipType) companyInfo.level = membershipType;
  payload.companyInfo = companyInfo;
  payload.selectedRegisterCompanyType = null;
  payload.id = accountId;

  const pi = payload.personalInfo;
  if (pi && typeof pi === "object" && !Array.isArray(pi)) {
    const personal = pi as Record<string, unknown>;
    if (!personal.previouslyRegistered) {
      delete personal.previouslyRegistered;
      delete personal.previouslyRegisteredEmail;
    }
  }

  return payload;
}
